import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PostAuthLoadError, UserService } from '../../../core/services/user.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { CredentialPromptService } from './../../../core/services/credential-prompt.service';
//import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { TutorialSection } from 'src/app/core/tutorial/tutorial.models';
import * as moment from 'moment';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { takeRemoatReturn } from '../../../core/utils/remoat-return.util';
import { takeLoginReturn } from '../../../core/utils/login-return.util';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BaseComponent implements OnInit {
  loginForm: FormGroup;
  showRegister = false;
  showForgot = false;
  showPassword = false;
  isNative: boolean = (environment as any).isNative === true;

  /**
   * True while a login request is in flight. Guards onLogin() and disables the submit button.
   *
   * Without it, every click submitted the form again: production recorded 15 cases of one user
   * creating two sessions within 5 seconds (one within 1ms, one user three times in 4.1s), all
   * from the same browser and IP. Each duplicate costs a full bcrypt comparison and leaves an
   * abandoned session row that reads like a concurrent session in the audit trail.
   */
  isLoggingIn = false;

  patchEmail?: string;
  patchType?: string;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private credentialPromptService: CredentialPromptService,
    //private tutorialService: TutorialService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  ngOnInit() {
    // Check URL for redirects and staging environment
    this.checkUrlAndRedirect();

    // Clears the in-memory profile ONLY. This deliberately no longer signs the user out:
    // `setUserProfile(null)` used to call `logout()`, so simply constructing this component wiped
    // `localStorage`. Harmless when someone arrives to sign in, destructive at any other moment —
    // a login that completed while a `window.location.reload()` was still pending had its token
    // deleted by the login screen being rebuilt, landing the user back here already
    // authenticated as far as the server was concerned. Every sign-out is now explicit, via
    // `UserService.logoutAndRedirect()`.
    this.userService.setUserProfile(null);
    this.route.queryParams.subscribe(params => {
      const inviteToken = params['inviteToken'];
      const register = params['register'];
      // A personal sign-up link from REMOAT: kept for the registration form to send with the new account.
      const signup = params['signup'];
      if (typeof signup === 'string' && /^[A-Za-z0-9_-]{43}$/.test(signup)) {
        sessionStorage.setItem('remoatSignup', signup);
      }
      if (inviteToken) {
        // Store the inviteToken temporarily
        localStorage.setItem('inviteToken', inviteToken);
      }
      if (register && !this.isNative) {
        this.showRegister = true;
        this.patchEmail = params['email'];
        this.patchType = params['role'];
        this.cd.detectChanges();
      }
    });

    this.prefillSavedCredentials();
  }

  private async prefillSavedCredentials(): Promise<void> {
    const saved = await this.credentialPromptService.getSavedCredentials();
    if (saved) {
      this.loginForm.patchValue({ email: saved.email, password: saved.password });
      this.cd.detectChanges();
    }
  }

  private checkUrlAndRedirect() {
    const currentUrl = window.location.hostname;

    // Check if we're on patient.medicai.co.za and redirect to doctor.medicai.co.za
    if (currentUrl === 'patient.medicai.co.za') {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const currentHash = window.location.hash;
      const newUrl = `https://doctor.medicai.co.za${currentPath}${currentSearch}${currentHash}`;
      window.location.href = newUrl;
      return;
    }

    // Check if URL contains "staging" and show popup
    if (currentUrl.includes('staging')) {
      this.showStagingEnvironmentPopup();
    }
  }

  private async showStagingEnvironmentPopup() {
    const confirmed = await this.feedbackService.showConfirmation(
      'STAGING ENVIRONMENT WARNING',
      'You are currently accessing the staging environment. This environment is used for testing new features and may contain incomplete or unstable functionality. \n\nPlease ensure you are using the correct environment for your intended purpose. \n\nDo you wish to continue with the staging environment?',
      [], 'Go To https://doctor.medicai.co.za', 'Stay Here'
    );
    if (!confirmed) {
      // Redirect to production
      window.location.href = 'https://doctor.medicai.co.za';
    }
  }

  onLogin() {
    // Already authenticating — ignore the extra submit. Covers a double-click on the button, an
    // impatient second click during the wait, and the onRegisterSuccess() path below, which calls
    // straight back into here.
    if (this.isLoggingIn) {
      return;
    }

    if (this.loginForm.valid) {
      this.isLoggingIn = true;
      const { email, password } = this.loginForm.value;
      const sub = this.userService.loginUser({ email, password }).pipe(
        // Reset on success, error AND unsubscribe. Doing it in the two handlers instead would
        // leave the button permanently disabled on any path that terminates without calling
        // either — and a login form that cannot be resubmitted is worse than the bug being fixed.
        //
        // Deliberately no cd.detectChanges() here: default change detection already covers this
        // (HttpClient runs inside the Angular zone), and on the success path the router has
        // usually destroyed this view by now, where detectChanges() would throw.
        finalize(() => { this.isLoggingIn = false; })
      ).subscribe(
        (response: any) => {
          if (response.success) {
            // Login successful — native-only: offer to save credentials to the
            // device's secure storage for faster sign-in next time. Fire-and-forget
            // so it doesn't block navigation. On web this is a no-op.
            void this.credentialPromptService.promptToSaveIfUnsaved(email, password);
            const inviteToken = localStorage.getItem('inviteToken');
            // A login started from REMOAT's "Sign in with MedicAI" goes back to hand the person over.
            const remoatReturn = takeRemoatReturn();
            if (remoatReturn) {
              this.router.navigateByUrl(remoatReturn);
            } else if (inviteToken) {
              // Navigate to the accept invite route with the inviteToken
              this.router.navigate(['/accept-invite', inviteToken]);
              // Remove the stored inviteToken
              localStorage.removeItem('inviteToken');
            } else {
              // Back to the page that needed the login, otherwise the schedule.
              const loginReturn = takeLoginReturn();
              if (loginReturn) this.router.navigateByUrl(loginReturn);
              else this.router.navigate(['/patients', 'schedule']);
            }
          } else {
            // Login failed, display error message
            console.error(response.message);
            if (response.code === 'AUTH_RATE_LIMIT_EXCEEDED') {
              this.showToastMessage('Too many login attempts. Please try again later');
            }
            else {
              this.showToastMessage('There was an error validating your details. Please check your details and try again');
            }
          }
          sub.unsubscribe();
        },
        (error: any) => {
          this.showLoginError(error);
          sub.unsubscribe();
        }
      );
    }
  }

  /**
   * Says which of the three round trips behind one "Login" click actually failed.
   *
   * Every failure used to read "There was an error validating your details. Please check your
   * details and try again", including the two that have nothing to do with the details entered.
   * Users acted on it — re-typing, resetting a password that was never wrong — while the real
   * problem was the connection. The password is only implicated when `POST /login` itself
   * refuses it.
   */
  private showLoginError(error: any): void {
    if (error instanceof PostAuthLoadError) {
      // The password was accepted and a token was issued, but /profile or /user-groups did not
      // come back. Sign out rather than leaving a token in storage with no profile loaded: it
      // keeps the invariant that sitting on the login page means holding no credential, and the
      // half-state is otherwise invisible until something fails oddly much later. The session
      // recorded server-side is closed by the same call.
      this.userService.logout();
      this.showToastMessage('You are signed in, but we could not load your profile. Please check your connection and try again');
      return;
    }

    // Optional chaining throughout: `error.error` is a ProgressEvent for a transport-level
    // failure and null for an empty response body, and reading `.code` straight off it threw
    // inside this handler — no toast, no navigation, just a button that flickered and a user
    // with no idea what happened. Exactly the bad-connection case this needs to report.
    if (error?.error?.code === 'AUTH_RATE_LIMIT_EXCEEDED') {
      this.showToastMessage('Too many login attempts. Please try again later');
      return;
    }

    // status 0 is the browser refusing to say more about a request that never completed:
    // offline, DNS failure, TLS failure, or a connection dropped mid-flight.
    if (error?.status === 0) {
      this.showToastMessage('We could not reach MedicAI. Please check your connection and try again');
      return;
    }

    if (error?.status >= 500) {
      this.showToastMessage('MedicAI is having trouble responding right now. Please try again in a moment');
      return;
    }

    this.showToastMessage('There was an error validating your details. Please check your details and try again');
  }

  onForgotPasswordSuccess() {
    this.showForgot = false;
  }

  onRegisterSuccess(values: any) {
    this.showRegister = false;

    if (values) {
      const { email, password } = values
      this.loginForm.patchValue({ email, password });
      this.onLogin();
    }
  }
}